import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RedirectCommand, Router, UrlTree } from '@angular/router';
import { LoggingService, VeterinarianService } from '@pxl/models/contracts';
import { AccountRoutingRouteNames, AppRoutingRouteNames } from '@pxl/route-names';
import { VeterinarianModel } from '@pxl/shared/generated';
import { defer, map } from 'rxjs';
import { RESOURCES } from '@pxl/shared/translation';

export const nonActiveVetGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const veterinarianService = inject(VeterinarianService);
    const loggingService = inject(LoggingService);
    const externalVetId = route.paramMap.get(AccountRoutingRouteNames.externalVeterinarianIdParam);

    const getAnonymousVeterinarian$ = defer(() => veterinarianService.getVeterinarianAsAnonymous(externalVetId, null));

    return getAnonymousVeterinarian$.pipe(
        map((veterinarian) => {
            const feature = getFeatureIdByUrlPath(route.url.join('/'));
            loggingService.info(`Non-active vet guard called for veterinarian: ${veterinarian?.id}`, veterinarian);
            const func = getRouteStrategyFunc(feature);
            return func(router, veterinarian);
        }),
    );
};

// Example of the expected response from the veterinarian features endpoint
// {
//   "userIsConnectedToVeterinarian": true,
//   "veterinarianFeatures": [
//     {
//       "featureId": "6d1845b7-7204-41e0-be08-0c7ef9211db0",
//       "value": true
//     },
//     {
//       "featureId": "f437f58f-6b52-4235-94a2-2df2c9717242",
//       "value": true
//     },
//     {
//       "featureId": "500610fd-7da1-400d-a1a5-29236ee05577",
//       "value": true
//     },
//     {
//       "featureId": "40bcd9e0-456f-4a6e-808c-f88430d033bd",
//       "value": true
//     },
//     {
//       "featureId": "9d60b02f-2907-4134-9ebe-39917fbc04e6",
//       "value": true
//     },
//     {
//       "featureId": "46d1418f-fcba-4381-ad4f-b8d5963c3e97",
//       "value": true
//     },
//     {
//       "featureId": "3df32cb1-c585-42c6-8c4f-2f4f26d1de0d",
//       "value": true
//     },
//     {
//       "featureId": "e838369d-a796-4a60-b993-76f7ff99cb94",
//       "value": true
//     },
//     {
//       "featureId": "5fd38d97-df4a-4249-a283-7e8e19aa966b",
//       "value": true
//     },
//     {
//       "featureId": "e98b9f79-f421-4f61-8f24-0a9a7c1fff58",
//       "value": true
//     },
//     {
//       "featureId": "dd6b22bd-df5d-4950-9a94-3755d6e18c3e",
//       "value": true
//     }
//   ],
//   "supportedEasyVetFeatures": {
//     "selfCheckInWithNote": false,
//     "linkageRequest": false,
//     "onlineCheckIn": false
//   }
// }

export const featurePaths: string[] = [
    AccountRoutingRouteNames.SelfCheckInRegistrationStart,
    AccountRoutingRouteNames.OnlineCheckInStart,
    AccountRoutingRouteNames.AppointmentRegistrationStart,
    AccountRoutingRouteNames.AppointmentRegistrationStart,
    AccountRoutingRouteNames.VeterinarianLinkageRequestRegistrationStart,
];

function getFeatureIdByUrlPath(urlPath: string): CondensedFeatureKey {
    const feature = featurePaths.find((path) => urlPath.includes(path));

    switch (feature) {
        case AccountRoutingRouteNames.SelfCheckInRegistrationStart:
            return condensedFeatureMap.SelfCheckIn;
        case AccountRoutingRouteNames.OnlineCheckInStart:
            return condensedFeatureMap.OnlineCheckIn;
        case AccountRoutingRouteNames.AppointmentRegistrationStart:
            return condensedFeatureMap.Appointments;
        case AccountRoutingRouteNames.VeterinarianLinkageRequestRegistrationStart:
            return condensedFeatureMap.RequestVeterinarianLinkage;
        default:
            throw new Error(`Feature ID not found for URL path: ${urlPath}`);
    }
}

export type FeatureIdKey =
    | 'AppointmentsForLinkedUsers'
    | 'AppointmentsForUnknownUsers'
    | 'SelfCheckIn'
    | 'RequestVeterinarianLinkage'
    | 'OnlineCheckIn';

const condensedFeatureMap = {
    Appointments: 'Appointments',
    SelfCheckIn: 'SelfCheckIn',
    RequestVeterinarianLinkage: 'RequestVeterinarianLinkage',
    OnlineCheckIn: 'OnlineCheckIn',
} as const;

type CondensedFeatureKey = keyof typeof condensedFeatureMap;

const selfCheckinAllowedFunc = (router: Router, veterinarian: VeterinarianModel) => {
    return defaultFunction(router, veterinarian);
};

const onlineCheckinAllowedFunc = (router: Router, veterinarian: VeterinarianModel) => {
    return defaultFunction(router, veterinarian);
};

const requestVeterinarianLinkageAllowedFunc = (router: Router, veterinarian: VeterinarianModel) => {
    return defaultFunction(router, veterinarian);
};

const appointmentsAllowedFunc = (router: Router, veterinarian: VeterinarianModel) => {
    return defaultFunction(router, veterinarian);
};

type RouteStrategyFunc = (router: Router, veterinarian?: VeterinarianModel) => boolean | UrlTree | RedirectCommand;

const routeStrategyFuncMap: {
    [k in CondensedFeatureKey]: RouteStrategyFunc;
} = {
    [condensedFeatureMap.SelfCheckIn]: selfCheckinAllowedFunc,
    [condensedFeatureMap.OnlineCheckIn]: onlineCheckinAllowedFunc,
    [condensedFeatureMap.RequestVeterinarianLinkage]: requestVeterinarianLinkageAllowedFunc,
    [condensedFeatureMap.Appointments]: appointmentsAllowedFunc,
};

function getRouteStrategyFunc(featureId: CondensedFeatureKey): RouteStrategyFunc {
    if (routeStrategyFuncMap[featureId]) {
        return routeStrategyFuncMap[featureId];
    }
    throw new Error(`No strategy function found for route: ${featureId}`);
}

function generateResources() {
    return {
        title: RESOURCES.petsxl.ui.non_active_vet.error.title,
        message: RESOURCES.petsxl.ui.non_active_vet.error.message,
    };
}

function defaultFunction(router: Router, veterinarian: VeterinarianModel | null): boolean | UrlTree | RedirectCommand {
    const urlTreeNotAllowed = router.createUrlTree([AppRoutingRouteNames.Error]);
    const isNotAllowedRedirectCommand = new RedirectCommand(urlTreeNotAllowed, {
        state: generateResources(),
    });

    return veterinarian?.isActive || isNotAllowedRedirectCommand;
}
